import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
    { label: "Home", href: "#hero", testid: "nav-home-link" },
    { label: "Il Dottore", href: "#il-dottore", testid: "nav-dottore-link" },
    { label: "Approccio", href: "#approccio", testid: "nav-approccio-link" },
    { label: "Percorso", href: "#percorso", testid: "nav-percorso-link" },
    { label: "Recensioni", href: "#recensioni", testid: "nav-recensioni-link" },
    { label: "Contatti", href: "#contatti", testid: "nav-contatti-link" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);

    const go = (e, href) => {
        e.preventDefault();
        setOpen(false);
        scrollToId(href);
    };

    return (
        <>
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-cream/85 border-b border-ink/10">
            <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 sm:px-12 lg:px-20 py-4" aria-label="Navigazione principale">
                <a
                    href="#hero"
                    data-testid="nav-logo"
                    onClick={(e) => go(e, "#hero")}
                    className="leading-tight"
                >
                    <span className="block font-serif text-lg sm:text-xl font-semibold text-ink">
                        Dott. Giovanni De Lorenzo
                    </span>
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-sage font-medium">
                        Psicologo · Psicoterapeuta
                    </span>
                </a>

                <ul className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <li key={l.href}>
                            <a
                                href={l.href}
                                data-testid={l.testid}
                                onClick={(e) => go(e, l.href)}
                                className="text-sm font-medium text-ink/70 hover:text-petrol transition-colors duration-300"
                            >
                                {l.label}
                            </a>
                        </li>
                    ))}
                    <li>
                        <Link
                            to="/riflessioni"
                            data-testid="nav-riflessioni-link"
                            className="text-sm font-medium text-ink/70 hover:text-petrol transition-colors duration-300"
                        >
                            Riflessioni
                        </Link>
                    </li>
                </ul>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="nav-book-consultation-btn"
                        onClick={(e) => go(e, "#contatti")}
                        className="hidden sm:inline-flex items-center rounded-full bg-petrol text-cream px-6 py-2.5 text-sm font-semibold hover:bg-petrol-soft hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                    >
                        Prenota un colloquio
                    </button>
                    <button
                        data-testid="nav-mobile-menu-btn"
                        onClick={() => setOpen(true)}
                        className="lg:hidden p-2 text-ink"
                        aria-label="Apri il menu"
                    >
                        <Menu size={24} />
                    </button>
                </div>
            </nav>
        </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-50 bg-petrol/95 backdrop-blur-md lg:hidden"
                        data-testid="nav-mobile-drawer"
                    >
                        <div className="flex justify-end p-6">
                            <button
                                data-testid="nav-mobile-close-btn"
                                onClick={() => setOpen(false)}
                                className="p-2 text-cream"
                                aria-label="Chiudi il menu"
                            >
                                <X size={28} />
                            </button>
                        </div>
                        <ul className="flex flex-col items-center gap-7 mt-8">
                            {LINKS.map((l, i) => (
                                <motion.li
                                    key={l.href}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.08 * i, duration: 0.4 }}
                                >
                                    <a
                                        href={l.href}
                                        data-testid={`${l.testid}-mobile`}
                                        onClick={(e) => go(e, l.href)}
                                        className="font-serif text-3xl text-cream"
                                    >
                                        {l.label}
                                    </a>
                                </motion.li>
                            ))}
                            <motion.li
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.4 }}
                            >
                                <Link
                                    to="/riflessioni"
                                    data-testid="nav-riflessioni-link-mobile"
                                    onClick={() => setOpen(false)}
                                    className="font-serif text-3xl text-cream"
                                >
                                    Riflessioni
                                </Link>
                            </motion.li>
                            <motion.li
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.58, duration: 0.4 }}
                            >
                                <button
                                    data-testid="nav-book-consultation-btn-mobile"
                                    onClick={(e) => go(e, "#contatti")}
                                    className="mt-4 rounded-full bg-cream text-petrol px-8 py-3 text-base font-semibold"
                                >
                                    Prenota un colloquio
                                </button>
                            </motion.li>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
